// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { apiClient } from './client'
import { tokenStorage } from '@/lib/auth/tokenStorage'
import { API_BASE_URL } from '@/config/api'
import type { SDFDocument, AuditEntry, DocumentFilter } from '@/types'
import type { PaginatedResponse } from '@/types'

export const documentsApi = {
  list: (filter?: DocumentFilter) =>
    apiClient.get<PaginatedResponse<SDFDocument>>('/v1/documents', filter as Record<string, string>),

  getById: (id: string) =>
    apiClient.get<SDFDocument>(`/v1/documents/${id}`),

  delete: (id: string) =>
    apiClient.delete(`/v1/documents/${id}`),

  verify: (id: string) =>
    apiClient.post<{ valid: boolean; details: string }>(`/v1/documents/${id}/verify`),

  getAuditLog: (id: string) =>
    apiClient.get<AuditEntry[]>(`/v1/documents/${id}/audit`),

  upload: async (
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<SDFDocument> => {
    const formData = new FormData()
    formData.append('file', file)

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const at = tokenStorage.getAccessToken()

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText) as SDFDocument)
        } else {
          const body = JSON.parse(xhr.responseText) as { message: string }
          reject(new Error(body.message))
        }
      }

      xhr.onerror = () => reject(new Error('Upload failed'))
      xhr.open('POST', `${API_BASE_URL}/v1/documents/upload`)
      if (at) xhr.setRequestHeader('Authorization', `Bearer ${at}`)
      xhr.send(formData)
    })
  },

  getDownloadUrl: (id: string): string =>
    `${API_BASE_URL}/v1/documents/${id}/download`,
}
